import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../Actions/axios.config';

export const postKeys = {
    all: ['posts'],
    lists: () => [...postKeys.all, 'list'],
    list: (filters) => [...postKeys.lists(), { filters }],
    details: () => [...postKeys.all, 'detail'],
    detail: (id) => [...postKeys.details(), id],
};

export const usePosts = (filters = {}) => {
    return useInfiniteQuery({
        queryKey: postKeys.list(filters),
        queryFn: async ({ pageParam = 1 }) => {
            const { data } = await axios.get('/posts/getPosts', {
                params: { ...filters, page: pageParam, limit: 10 }
            });
            return data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage.hasNextPage) {
                return lastPage.page + 1;
            }
            return undefined;
        },
    });
};

export const usePost = (postId) => {
    return useQuery({
        queryKey: postKeys.detail(postId),
        queryFn: async () => {
            const { data } = await axios.get(`/posts/getPosts/${postId}`);
            return data.post;
        },
        enabled: !!postId,
    });
};

export const useCreatePost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (newPost) => axios.post('/posts/createPost', newPost),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: postKeys.lists() });
        },
    });
};

export const useUpdatePost = (postId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (updatedPost) => axios.put(`/posts/update/${postId}`, updatedPost),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: postKeys.lists() });
            queryClient.invalidateQueries({ queryKey: postKeys.detail(postId) });
        },
    });
};

export const useDeletePost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (postId) => axios.delete(`/posts/delete/${postId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: postKeys.lists() });
        },
    });
};

export const useMyPosts = (userId) => {
    return useQuery({
        queryKey: [...postKeys.lists(), 'my', userId],
        queryFn: async () => {
            const { data } = await axios.get(`/posts/getPosts/my/${userId}`);
            return data.posts;
        },
        enabled: !!userId,
    });
};

export const useSavedPosts = () => {
    return useQuery({
        queryKey: [...postKeys.lists(), 'saved'],
        queryFn: async () => {
            const { data } = await axios.get('/posts/getPosts/bookmarked');
            return data.posts;
        },
    });
};

export const useLikedPosts = (userId) => {
    return useQuery({
        queryKey: [...postKeys.lists(), 'liked', userId],
        queryFn: async () => {
            const { data } = await axios.get(`/posts/getPosts/Liked/${userId}`);
            return data.posts;
        },
        enabled: !!userId,
    });
};
